import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import EnterpriseIcon, { CancelIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React__default, { useState, useRef, useEffect } from 'react';
import ReactDom from 'react-dom';
import useActiveElement from '../hooks/useActiveElement.js';
import DisableBackgroundScroll from '../utils/DisableBackgroundScroll.js';
import FocusTrap from '../utils/FocusTrap.js';
import alertUserOfDeprecatedProps from '../utils/deprecation.js';
import useCanvasId from '../utils/useCanvasId.js';
import Button from './Button.js';
import Heading from './Heading.js';
import Overlay from './Overlay.js';

var _excluded = ["aria-label", "children", "className", "headingText", "footer", "isVisible", "onRefuse", "size", "parentElementId", "scrollBehavior", "id"];
var Modal = function Modal(_ref) {
  var ariaLabel = _ref['aria-label'],
    children = _ref.children,
    className = _ref.className,
    headingText = _ref.headingText,
    footer = _ref.footer,
    isVisible = _ref.isVisible,
    onRefuse = _ref.onRefuse,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'normal' : _ref$size,
    _ref$parentElementId = _ref.parentElementId,
    parentElementId = _ref$parentElementId === void 0 ? 'canvasPortalContainer' : _ref$parentElementId,
    _ref$scrollBehavior = _ref.scrollBehavior,
    scrollBehavior = _ref$scrollBehavior === void 0 ? 'default' : _ref$scrollBehavior,
    id = _ref.id,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  alertUserOfDeprecatedProps('Modal', otherProps);

  // Stores a DOM reference to the parent that we should attach this modal to
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    portalRef = _useState2[0],
    setPortalRef = _useState2[1];
  // Stores a reference to this component. In the event we can't find the parentElementId to attach the modal to,
  // we instead use this reference to attach to the parent of this component instead.
  var thisComponent = useRef(null);
  useEffect(function () {
    // We need to ensure that the parent has rendered before attempting to get a reference to it.
    // useEffect ensures that has occured.
    if (thisComponent.current) {
      var el = document.getElementById(parentElementId) || thisComponent.current.parentElement; // If parentElementId is not found, default to the parent of this component
      setPortalRef(el);
    }
  }, [thisComponent, parentElementId]);

  // Cache the current focused dom element across re-renders
  var focusedElRef = useActiveElement(isVisible);
  var finalClasses = classnames('C-Modal', _defineProperty(_defineProperty({}, "--size-".concat(size), size), "isVisible", isVisible), className);
  var modalContainerClass = classnames('C-Modal__container', _defineProperty({}, "--scroll-".concat(scrollBehavior), scrollBehavior));
  var modalContentClass = classnames('C-Modal__container__content', _defineProperty({}, "--scroll-".concat(scrollBehavior), scrollBehavior));
  var onRefuseModal = function onRefuseModal(event) {
    event.stopPropagation();
    typeof onRefuse === 'function' && onRefuse(event);
  };
  var onRefuseModalScrollContainer = function onRefuseModalScrollContainer(event) {
    if (event.target.classList.contains('C-Modal__scrollContainer')) {
      // Avoids unintentionally closing the modal by preventing onRefuseModal from firing
      // when a users "releases" a mouse-drag/selection outside the modal element.
      var highlightedText = false;
      if (window.getSelection) {
        highlightedText = window.getSelection().toString();
      } else if (document.selection && document.selection.type != 'Control') {
        highlightedText = document.selection.createRange().text;
      }
      if (!highlightedText) {
        // No selection exists, allow modal to close
        onRefuseModal(event);
      }
    }
  };
  var generatedA11yId = useCanvasId(id, 'modal');
  var header = null;
  if (headingText) {
    header = /*#__PURE__*/React__default.createElement(Heading, {
      id: generatedA11yId,
      size: 1,
      tabIndex: "-1",
      className: "hc-pt-normal hc-pb-normal hc-pl-normal hc-pr-2x hc-fs-lg"
    }, headingText);
  }
  var modalFooter = null;
  if (footer) {
    modalFooter = /*#__PURE__*/React__default.createElement("div", {
      className: "hc-pa-sm"
    }, footer);
  }
  var closeBtn = /*#__PURE__*/React__default.createElement(Button, {
    className: "C-Modal__closeButton",
    size: "dense",
    iconOnly: true,
    type: "ghost",
    "data-testid": otherProps['data-testid'] ? "".concat(otherProps['data-testid'], "_closeButton") : null,
    "aria-label": "Close Modal",
    onClick: onRefuseModal
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: CancelIcon
  }));
  if (!portalRef) {
    return /*#__PURE__*/React__default.createElement("div", {
      ref: thisComponent
    });
  }
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ReactDom.createPortal( /*#__PURE__*/React__default.createElement("div", _extends({
    className: finalClasses,
    "aria-labelledby": generatedA11yId
  }, otherProps), /*#__PURE__*/React__default.createElement(DisableBackgroundScroll, {
    isActive: isVisible
  }), /*#__PURE__*/React__default.createElement(FocusTrap, {
    active: isVisible,
    returnFocusTo: focusedElRef,
    className: "C-Modal__scrollContainer",
    contentClassName: modalContainerClass,
    onClick: onRefuseModalScrollContainer,
    onEscape: onRefuseModal,
    "aria-label": ariaLabel || 'Modal'
  }, header, closeBtn, /*#__PURE__*/React__default.createElement("div", {
    className: modalContentClass
  }, children), modalFooter), /*#__PURE__*/React__default.createElement(Overlay, {
    type: "subtle",
    onClick: onRefuseModal,
    isVisible: isVisible
  })), portalRef));
};
Modal.displayName = 'Modal';

export { Modal, Modal as default };
